"use client";

import { ContextMenu as ContextMenuPrimitive } from "@base-ui/react/context-menu";
import { useRef } from "react";
import type * as React from "react";
import type { Menu as MenuPrimitive } from "@base-ui/react/menu";
import { cva, type VariantProps } from "../../lib/cva";
import { isDragInFlight } from "../../lib/dnd-attrs";
import { Icon } from "./icon";
import { useKeyboardHints } from "../../hooks/use-keyboard-hints";
import { cn } from "../../lib/utils";
import type { Omittable } from "../../lib/props";
import {
  MENU_ACTION_ROW_CLASS,
  MENU_ACTION_ROW_ITEM_CLASS,
  MENU_BUTTON_ITEM_CLASS,
  MenuCloseProvider,
  menuPopupVariants,
  releaseKeyboardToTextField,
  resolveMenuMinWidth,
  withMenuRowDisabledReason,
  type MenuPopupPadding,
  type MenuPopupWidth,
  type MenuRowDisabledReasonProps,
} from "./menu";
import { SWITCH_THUMB_FILL_CLASS, SWITCH_TRACK_STATE_CLASS } from "./switch";

/**
 * A context menu never opens over a drag in flight. A finger's hold lifts a
 * draggable at 400ms (`DND_TOUCH_ACTIVATION`) and Base UI's trigger fires its
 * own long press at 500ms on a timer nothing clears once the drag is live —
 * so the menu would open on top of the card the same finger is already
 * moving. dnd-kit prevents the native `contextmenu` during a drag; this
 * refuses the synthetic one. The caller's `onOpenChange` still sees every
 * other change.
 */
export function ContextMenu({
  onOpenChange,
  actionsRef,
  ...props
}: ContextMenuPrimitive.Root.Props): React.ReactElement {
  const ownActions = useRef<MenuPrimitive.Root.Actions | null>(null);
  const actions = actionsRef ?? ownActions;
  return (
    <MenuCloseProvider actionsRef={actions}>
      <ContextMenuPrimitive.Root
        actionsRef={actions}
        onOpenChange={(open, details) => {
          if (open && isDragInFlight()) {
            details.cancel();
            return;
          }
          onOpenChange?.(open, details);
        }}
        {...props}
      />
    </MenuCloseProvider>
  );
}

export const ContextMenuPortal: typeof ContextMenuPrimitive.Portal =
  ContextMenuPrimitive.Portal;

export function ContextMenuTrigger({
  className,
  children,
  ...props
}: ContextMenuPrimitive.Trigger.Props): React.ReactElement {
  return (
    <ContextMenuPrimitive.Trigger
      className={className}
      data-slot="context-menu-trigger"
      {...props}
    >
      {children}
    </ContextMenuPrimitive.Trigger>
  );
}

export function ContextMenuPopup({
  children,
  className,
  sideOffset = 4,
  align = "center",
  alignOffset,
  side = "bottom",
  anchor,
  portalProps,
  padding = "sm",
  width,
  style,
  onKeyDown,
  ...props
}: ContextMenuPrimitive.Popup.Props & Omittable<{
  align?: ContextMenuPrimitive.Positioner.Props["align"];
  sideOffset?: ContextMenuPrimitive.Positioner.Props["sideOffset"];
  alignOffset?: ContextMenuPrimitive.Positioner.Props["alignOffset"];
  side?: ContextMenuPrimitive.Positioner.Props["side"];
  anchor?: ContextMenuPrimitive.Positioner.Props["anchor"];
  portalProps?: ContextMenuPrimitive.Portal.Props;
  padding?: MenuPopupPadding;
  /** Width floor by menu kind — see MenuPopupWidth. */
  width?: MenuPopupWidth;
  /** Plain style object only — the width resolver merges into it. */
  style?: React.CSSProperties;
}>): React.ReactElement {
  return (
    <ContextMenuPortal {...portalProps}>
      <ContextMenuPrimitive.Positioner
        align={align}
        alignOffset={alignOffset}
        anchor={anchor}
        className="z-50"
        data-slot="context-menu-positioner"
        side={side}
        sideOffset={sideOffset}
      >
        <ContextMenuPrimitive.Popup
          className={cn(
            "relative flex not-[class*='w-']:min-w-32 origin-(--transform-origin) rounded-lg border bg-popover not-dark:bg-clip-padding shadow-popover outline-none before:pointer-events-none before:absolute before:inset-0 before:rounded-[calc(var(--radius-lg)-1px)] before:shadow-[0_1px_--theme(--color-black/4%)] focus:outline-none dark:before:shadow-[0_-1px_--theme(--color-white/6%)]",
            className,
          )}
          // Portalled, so it inherits no surface scope — names its own, and
          // descendant `border-border` draws the popover-tuned hairline.
          data-on="popover"
          data-slot="context-menu-popup"
          data-width={width}
          onKeyDown={(event) => {
            releaseKeyboardToTextField(event);
            onKeyDown?.(event);
          }}
          // A right-click menu is the same object as the menu its trigger would
          // open, so it answers the same rungs. See MenuPopupWidth.
          style={resolveMenuMinWidth(width, undefined, style)}
          {...props}
        >
          <div
            className={menuPopupVariants({ padding })}
            data-padding={padding}
          >
            {children}
          </div>
        </ContextMenuPrimitive.Popup>
      </ContextMenuPrimitive.Positioner>
    </ContextMenuPortal>
  );
}

export function ContextMenuGroup(
  props: ContextMenuPrimitive.Group.Props,
): React.ReactElement {
  return (
    <ContextMenuPrimitive.Group data-slot="context-menu-group" {...props} />
  );
}

/** The context-menu twin of `MenuActionRow` — same strip, this menu kind's
 *  data-slot so the two trees stay greppable apart. */
export function ContextMenuActionRow({
  className,
  ...props
}: React.ComponentProps<"div">): React.ReactElement {
  return (
    <div
      className={cn(MENU_ACTION_ROW_CLASS, className)}
      data-slot="context-menu-action-row"
      role="group"
      {...props}
    />
  );
}

export function ContextMenuActionRowItem({
  className,
  disabledReason,
  ...props
}: ContextMenuPrimitive.Item.Props &
  MenuRowDisabledReasonProps): React.ReactElement {
  return withMenuRowDisabledReason(
    (reasonClass) => (
      <ContextMenuPrimitive.Item
        className={cn(MENU_ACTION_ROW_ITEM_CLASS, reasonClass, className)}
        data-slot="context-menu-action-row-item"
        {...props}
      />
    ),
    props.disabled,
    disabledReason,
  );
}

/** The context-menu twin of `MenuButtonItem` — the promoted full-width
 *  primary-button action at the menu's foot. */
export function ContextMenuButtonItem({
  className,
  ...props
}: ContextMenuPrimitive.Item.Props): React.ReactElement {
  return (
    <ContextMenuPrimitive.Item
      className={cn(MENU_BUTTON_ITEM_CLASS, className)}
      data-slot="context-menu-button-item"
      {...props}
    />
  );
}

export function ContextMenuItem({
  className,
  inset,
  variant = "default",
  disabledReason,
  ...props
}: ContextMenuPrimitive.Item.Props & MenuRowDisabledReasonProps & Omittable<{
  inset?: boolean;
  variant?: "default" | "destructive";
}>): React.ReactElement {
  return withMenuRowDisabledReason(
    (reasonClass) => (
      <ContextMenuPrimitive.Item
        className={cn(
          "flex min-h-8 cursor-pointer select-none items-center gap-2 rounded-sm px-2 py-1.5 text-sm text-foreground outline-none data-disabled:pointer-events-none data-disabled:cursor-not-allowed data-highlighted:bg-wash-hover data-inset:ps-8 data-[variant=destructive]:not-data-disabled:text-destructive-foreground data-highlighted:text-foreground data-disabled:opacity-64 [&>svg:not([class*='opacity-'])]:opacity-80 [&>svg:not([class*='size-'])]:size-4 [&>svg]:pointer-events-none [&>svg]:-mx-0.5 [&>svg]:shrink-0",
          reasonClass,
          className,
        )}
        data-inset={inset}
        data-slot="context-menu-item"
        data-variant={variant}
        {...props}
      />
    ),
    props.disabled,
    disabledReason,
  );
}

export function ContextMenuLinkItem({
  className,
  inset,
  variant = "default",
  closeOnClick = true,
  ...props
}: ContextMenuPrimitive.LinkItem.Props & Omittable<{
  inset?: boolean;
  variant?: "default" | "destructive";
}>): React.ReactElement {
  return (
    <ContextMenuPrimitive.LinkItem
      className={cn(
        "flex min-h-8 cursor-pointer select-none items-center gap-2 rounded-sm px-2 py-1.5 text-sm text-foreground outline-none data-disabled:pointer-events-none data-disabled:cursor-not-allowed data-highlighted:bg-wash-hover data-inset:ps-8 data-[variant=destructive]:not-data-disabled:text-destructive-foreground data-highlighted:text-foreground data-disabled:opacity-64 [&>svg:not([class*='opacity-'])]:opacity-80 [&>svg:not([class*='size-'])]:size-4 [&>svg]:pointer-events-none [&>svg]:-mx-0.5 [&>svg]:shrink-0",
        className,
      )}
      closeOnClick={closeOnClick}
      data-inset={inset}
      data-slot="context-menu-link-item"
      data-variant={variant}
      {...props}
    />
  );
}

export const contextMenuCheckboxItemVariants = cva(
  "grid min-h-8 in-data-[side=none]:min-w-[calc(var(--anchor-width)+1.25rem)] cursor-pointer items-center gap-2 rounded-sm py-1.5 ps-2 text-sm text-foreground outline-none data-disabled:pointer-events-none data-disabled:cursor-not-allowed data-highlighted:bg-wash-hover data-highlighted:text-foreground data-disabled:opacity-64 [&_svg:not([class*='size-'])]:size-4 [&_svg]:pointer-events-none [&_svg]:shrink-0",
  {
    variants: {
      variant: {
        default: "grid-cols-[.75rem_1fr] pe-4",
        switch: "grid-cols-[1fr_auto] gap-4 pe-1.5",
      },
    },
    defaultVariants: {
      variant: "default",
    },
  },
);

export function ContextMenuCheckboxItem({
  className,
  children,
  checked,
  variant = "default",
  disabledReason,
  ...props
}: ContextMenuPrimitive.CheckboxItem.Props &
  MenuRowDisabledReasonProps &
  Omittable<{
    variant?: VariantProps<typeof contextMenuCheckboxItemVariants>["variant"];
  }>): React.ReactElement {
  return withMenuRowDisabledReason(
    (reasonClass) => (
    <ContextMenuPrimitive.CheckboxItem
      checked={checked}
      className={cn(
        contextMenuCheckboxItemVariants({ variant }),
        reasonClass,
        className,
      )}
      data-slot="context-menu-checkbox-item"
      {...props}
    >
      {variant === "switch" ? (
        <>
          <span className="col-start-1">{children}</span>
          {/* Wears the standalone Switch's state fills so the two faces
              cannot drift. */}
          <ContextMenuPrimitive.CheckboxItemIndicator
            className={cn(
              "inset-shadow-[0_1px_--theme(--color-black/4%)] inline-flex h-[calc(var(--thumb-size)+2px)] w-[calc(var(--thumb-size)*2-2px)] shrink-0 items-center rounded-full p-px outline-none transition-[background-color,box-shadow] duration-base [--thumb-size:--spacing(3)] focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1 focus-visible:ring-offset-background data-disabled:opacity-64",
              SWITCH_TRACK_STATE_CLASS,
            )}
            keepMounted
          >
            <span
              className={cn(
                "pointer-events-none block aspect-square h-full in-[[data-slot=context-menu-checkbox-item][data-checked]]:origin-[var(--thumb-size)_50%] origin-left in-[[data-slot=context-menu-checkbox-item][data-checked]]:translate-x-[calc(var(--thumb-size)-4px)] in-[[data-slot=context-menu-checkbox-item]:active]:not-data-disabled:scale-x-110 in-[[data-slot=context-menu-checkbox-item]:active]:rounded-[var(--thumb-size)/calc(var(--thumb-size)*1.10)] rounded-(--thumb-size) shadow-sm/5 will-change-transform [transition:translate_.15s,border-radius_.15s,scale_.1s_.1s,transform-origin_.15s,background-color_.15s]",
                SWITCH_THUMB_FILL_CLASS,
              )}
            />
          </ContextMenuPrimitive.CheckboxItemIndicator>
        </>
      ) : (
        <>
          <ContextMenuPrimitive.CheckboxItemIndicator className="col-start-1 -ms-0.5">
            <svg
              aria-hidden="true"
              fill="none"
              height="24"
              stroke="currentColor"
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="2"
              viewBox="0 0 24 24"
              width="24"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path d="M5.252 12.7 10.2 18.63 18.748 5.37" />
            </svg>
          </ContextMenuPrimitive.CheckboxItemIndicator>
          <span className="col-start-2">{children}</span>
        </>
      )}
    </ContextMenuPrimitive.CheckboxItem>
    ),
    props.disabled,
    disabledReason,
  );
}

export function ContextMenuRadioGroup(
  props: ContextMenuPrimitive.RadioGroup.Props,
): React.ReactElement {
  return (
    <ContextMenuPrimitive.RadioGroup
      data-slot="context-menu-radio-group"
      {...props}
    />
  );
}

export function ContextMenuRadioItem({
  className,
  children,
  ...props
}: ContextMenuPrimitive.RadioItem.Props): React.ReactElement {
  return (
    <ContextMenuPrimitive.RadioItem
      className={cn(
        "grid min-h-8 in-data-[side=none]:min-w-[calc(var(--anchor-width)+1.25rem)] cursor-pointer grid-cols-[.75rem_1fr] items-center gap-2 rounded-sm py-1.5 ps-2 pe-4 text-sm text-foreground outline-none data-disabled:pointer-events-none data-disabled:cursor-not-allowed data-highlighted:bg-wash-hover data-highlighted:text-foreground data-disabled:opacity-64 [&_svg:not([class*='size-'])]:size-4 [&_svg]:pointer-events-none [&_svg]:shrink-0",
        className,
      )}
      data-slot="context-menu-radio-item"
      {...props}
    >
      <ContextMenuPrimitive.RadioItemIndicator className="col-start-1 -ms-0.5">
        <svg
          aria-hidden="true"
          fill="none"
          height="24"
          stroke="currentColor"
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth="2"
          viewBox="0 0 24 24"
          width="24"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path d="M5.252 12.7 10.2 18.63 18.748 5.37" />
        </svg>
      </ContextMenuPrimitive.RadioItemIndicator>
      <span className="col-start-2">{children}</span>
    </ContextMenuPrimitive.RadioItem>
  );
}

export function ContextMenuGroupLabel({
  className,
  inset,
  ...props
}: ContextMenuPrimitive.GroupLabel.Props & Omittable<{
  inset?: boolean;
}>): React.ReactElement {
  return (
    <ContextMenuPrimitive.GroupLabel
      className={cn(
        "px-2 py-1.5 font-medium text-muted-foreground text-xs data-inset:ps-8",
        className,
      )}
      data-inset={inset}
      data-slot="context-menu-label"
      {...props}
    />
  );
}

export function ContextMenuSeparator({
  className,
  ...props
}: ContextMenuPrimitive.Separator.Props): React.ReactElement {
  return (
    <ContextMenuPrimitive.Separator
      // Full-bleed: the rule reaches the popup edge whatever the content
      // padding is (see MenuSeparator), with ample vertical breathing room —
      // except in a `padding="none"` popup, where sections own all their
      // insets and the separator adds no air of its own.
      className={cn(
        "my-2 h-px bg-border in-data-[padding=none]:mx-0 in-data-[padding=none]:my-0 in-data-[padding=2xs]:-mx-1 in-data-[padding=sm]:-mx-2",
        className,
      )}
      data-slot="context-menu-separator"
      {...props}
    />
  );
}

/** The row's key binding. Nothing on a device with no keyboard to press it. */
export function ContextMenuShortcut({
  className,
  ...props
}: React.ComponentProps<"kbd">): React.ReactElement | null {
  if (!useKeyboardHints()) return null;
  return (
    <kbd
      className={cn(
        "ms-auto font-medium font-sans text-muted-foreground/72 text-xs tracking-widest",
        className,
      )}
      data-slot="context-menu-shortcut"
      {...props}
    />
  );
}

export function ContextMenuSub(
  props: ContextMenuPrimitive.SubmenuRoot.Props,
): React.ReactElement {
  return (
    <ContextMenuPrimitive.SubmenuRoot data-slot="context-menu-sub" {...props} />
  );
}

export function ContextMenuSubTrigger({
  className,
  inset,
  disabledReason,
  children,
  ...props
}: ContextMenuPrimitive.SubmenuTrigger.Props &
  MenuRowDisabledReasonProps &
  Omittable<{
    inset?: boolean;
  }>): React.ReactElement {
  return withMenuRowDisabledReason(
    (reasonClass) => (
      <ContextMenuPrimitive.SubmenuTrigger
        className={cn(
          "flex min-h-8 cursor-pointer items-center gap-2 rounded-sm px-2 py-1.5 text-sm text-foreground outline-none data-disabled:pointer-events-none data-disabled:cursor-not-allowed data-highlighted:bg-wash-hover data-popup-open:bg-wash-hover data-inset:ps-8 data-highlighted:text-foreground data-popup-open:text-foreground data-disabled:opacity-64 [&>svg:not(:last-child)]:-mx-0.5 [&_svg:not([class*='size-'])]:size-4 [&_svg]:pointer-events-none",
          reasonClass,
          className,
        )}
        data-inset={inset}
        data-slot="context-menu-sub-trigger"
        {...props}
      >
        {children}
        <Icon className="ms-auto -me-0.5 opacity-80" name="chevron-right" />
      </ContextMenuPrimitive.SubmenuTrigger>
    ),
    props.disabled,
    disabledReason,
  );
}

export function ContextMenuSubPopup({
  className,
  sideOffset = 0,
  alignOffset,
  align = "start",
  sheetTitle: _sheetTitle,
  ...props
}: ContextMenuPrimitive.Popup.Props & Omittable<{
  /** Accepted for parity with `MenuSubPopup` and dropped: a context menu's
   *  submenu keeps its side popup on every viewport. */
  sheetTitle?: React.ReactNode;
  align?: ContextMenuPrimitive.Positioner.Props["align"];
  sideOffset?: ContextMenuPrimitive.Positioner.Props["sideOffset"];
  alignOffset?: ContextMenuPrimitive.Positioner.Props["alignOffset"];
  /** Forwarded to `ContextMenuPopup` — `"none"` for full-bleed content. */
  padding?: MenuPopupPadding;
  /** Width floor by menu kind — see MenuPopupWidth. */
  width?: MenuPopupWidth;
}>): React.ReactElement {
  const defaultAlignOffset = align !== "center" ? -5 : undefined;

  return (
    <ContextMenuPopup
      align={align}
      alignOffset={alignOffset ?? defaultAlignOffset}
      className={className}
      data-slot="context-menu-sub-content"
      side="inline-end"
      sideOffset={sideOffset}
      {...props}
    />
  );
}
